import { Extensions } from '@tiptap/react';
import type { Doc as YDoc } from 'yjs';
import { StarterKit } from '@tiptap/starter-kit';
import { TaskItem } from '@tiptap/extension-task-item';
import { TaskList } from '@tiptap/extension-task-list';
import { TextAlign } from '@tiptap/extension-text-align';
import { Typography } from '@tiptap/extension-typography';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import Highlight from '@tiptap/extension-highlight';
import { TrailingNode } from '@tiptap/extensions';
import { Emoji, gitHubEmojis } from '@tiptap/extension-emoji';
import { Mathematics } from '@tiptap/extension-mathematics';

import { Image } from 'containers/Tiptap/components/tiptap-node/image-node/image-node-extension';
import { RiskAssessmentsNode } from 'containers/Tiptap/components/tiptap-node/risk-assessments-node';
import { RiskAssessmentNode } from 'containers/Tiptap/components/tiptap-node/risk-assessment-node';
import { Link } from 'containers/Tiptap/components/tiptap-extension/link-extension';
import { Selection } from 'containers/Tiptap/components/tiptap-extension/selection-extension';
import { TableKit } from 'containers/Tiptap/components/tiptap-node/table-node/extensions/table-node-extension';
import { HorizontalRule } from 'containers/Tiptap/components/tiptap-node/horizontal-rule-node/horizontal-rule-node-extension';

import { createConvertKitExtension } from './convert-kit';
import { NumberingFormatsSync } from './numbering-formats-sync';
import { BulletListFormats } from './bullet-list-formats';
import { InlineCode } from './inline-code';

import './core-node-styles';

export interface CreateCoreExtensionsOptions {
  tableResizable?: boolean;
  ydoc?: YDoc | null;
}

export function createCoreExtensions({
  tableResizable = true,
  ydoc = null,
}: CreateCoreExtensionsOptions = {}): Extensions {
  return [
    StarterKit.configure({
      undoRedo: false,
      link: false,
      trailingNode: false,
      horizontalRule: false,
      code: false,
    }),
    HorizontalRule,
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    TaskList,
    TaskItem.configure({ nested: true }),
    Typography,
    Superscript,
    Subscript,
    Selection,
    TrailingNode.configure({
      node: 'paragraph',
      notAfter: ['paragraph'],
    }),
    Link.configure({ openOnClick: true }),
    InlineCode,
    Highlight.configure({ multicolor: true }),
    Image,
    RiskAssessmentsNode,
    RiskAssessmentNode,
    TableKit.configure({
      table: {
        resizable: tableResizable,
      },
    }),
    Emoji.configure({
      emojis: gitHubEmojis.filter((emoji) => !emoji.name.includes('regional')),
      forceFallbackImages: true,
    }),
    Mathematics,
    createConvertKitExtension(),
    BulletListFormats,
    NumberingFormatsSync.configure({ ydoc }),
  ];
}
